"use client";

import Image from "next/image";
import { useCallback, useRef, useState } from "react";
import { useBookCallPopup } from "@/components/booking/CcBookCallPopupProvider";

import { CcContactAntiqueHeroBackdrop } from "@/components/contact/CcContactAntiqueHeroBackdrop";
import {
  CcContactPillField,
  type ContactPillOption,
} from "@/components/contact/CcContactPillField";
import { CcContactSidebar } from "@/components/contact/CcContactSidebar";
import {
  CcContactParallaxFormFade,
  CcContactParallaxShell,
} from "@/components/contact/CcContactParallaxShell";
import { CcHeroLogoBand } from "@/components/home/CcHeroLogoBand";
import { CcAntiqueHeroPicture } from "@/components/layout/CcAntiqueHeroPicture";
import { CcAnimatedCheckbox } from "@/components/ui/CcAnimatedCheckbox";
import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { CcInternalLink } from "@/components/ui/CcInternalLink";
import pageCopy from "@/data/page_copy.json";
import { APP } from "@/lib/constants";
import { trackContactLead } from "@/lib/contact-lead-tracking";
import { t, tHtml } from "@/lib/content";
import {
  CONTACT_PAGE_VARIANT,
  contactVariantConfig,
  isSplitColumnContactVariant,
  type ContactFormTheme,
  type ContactPageVariant,
} from "@/lib/cc-contact-layout";
import { devSectionAttr } from "@/lib/dev/section-attr";
import { getCommonCopy } from "@/lib/common-copy";
import { bookCallOriginFromElement } from "@/lib/integrations/book-call-popup-origin";
import type { Lang, LocalizedString } from "@/lib/types";

type FormFields = {
  name: string;
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  website: string;
  message: string;
};

type FieldErrors = Partial<Record<keyof FormFields, boolean>>;

export type { ContactFormTheme } from "@/lib/cc-contact-layout";

type CcContactFormProps = {
  lang: Lang;
  variant?: ContactPageVariant;
  formTheme?: ContactFormTheme;
  showExtendedFields?: boolean;
  showBudgetFields?: boolean;
};

function getOurCompany(): string {
  const companyName =
    (APP.brand as { companyName?: string }).companyName ?? "";
  return companyName.split(" ")[0].replace(" ", "").toLowerCase();
}

function getVisitorJourney(): string | null {
  try {
    return localStorage.getItem("visitorJourney");
  } catch {
    return null;
  }
}

function validateForm(
  data: FormFields,
  options: { splitName: boolean; requireCompanyWebsite: boolean },
): FieldErrors {
  const errors: FieldErrors = {};
  if (options.splitName) {
    if (!data.firstName.trim()) errors.firstName = true;
    if (!data.lastName.trim()) errors.lastName = true;
  } else if (!data.name.trim()) {
    errors.name = true;
  }
  const email = data.email.trim();
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    errors.email = true;
  }
  if (options.requireCompanyWebsite) {
    if (!data.company.trim()) errors.company = true;
    if (!data.website.trim()) errors.website = true;
  }
  if (data.website.trim()) {
    try {
      const url = new URL(data.website.trim());
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        errors.website = true;
      }
    } catch {
      errors.website = true;
    }
  }
  if (!data.message.trim()) errors.message = true;
  return errors;
}

function submissionName(data: FormFields, splitName: boolean): string {
  if (splitName) {
    return `${data.firstName.trim()} ${data.lastName.trim()}`.trim();
  }
  return data.name.trim();
}

function cellClass(
  base: string,
  invalid: boolean,
): string {
  return `${base}${invalid ? " cc-contact__cell--invalid" : ""}`;
}

function buildPillOptions(
  order: string[],
  pills: Record<string, LocalizedString>,
): ContactPillOption[] {
  return order
    .filter((value) => pills[value])
    .map((value) => ({ value, label: pills[value] }));
}

function appendSubmissionMeta(
  message: string,
  budget: string,
  serviceLabels: string[],
  budgetLabel: string,
): string {
  const lines: string[] = [];
  if (serviceLabels.length > 0) {
    lines.push(`Services: ${serviceLabels.join(", ")}`);
  }
  if (budget) {
    lines.push(`Budget: ${budgetLabel}`);
  }
  if (lines.length === 0) {
    return message;
  }
  return `${message}\n\n---\n${lines.join("\n")}`;
}

export function CcContactForm({
  lang,
  variant = CONTACT_PAGE_VARIANT,
  formTheme = contactVariantConfig(variant).formTheme,
  showExtendedFields = contactVariantConfig(variant).showExtendedFields,
  showBudgetFields = contactVariantConfig(variant).showBudgetFields,
}: CcContactFormProps) {
  const bookCall = useBookCallPopup();
  const contactCopy = pageCopy.contact;
  const commonCopy = getCommonCopy(lang);
  const formCopy = pageCopy.contact.form;
  const servicePillOptions = buildPillOptions(
    formCopy.servicePillOrder,
    formCopy.servicePills,
  );
  const budgetPillOptions = buildPillOptions(
    formCopy.budgetPillOrder,
    formCopy.budgetPills,
  );
  const panelEyebrow = t(formCopy.panelEyebrow, lang);
  const panelLead = t(formCopy.panelLead, lang);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedBudget, setSelectedBudget] = useState("");
  const [formData, setFormData] = useState<FormFields>({
    name: "",
    firstName: "",
    lastName: "",
    email: "",
    company: "",
    website: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState<FieldErrors>({});
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [privacyPolicyError, setPrivacyPolicyError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const submissionPending = useRef(false);

  const updateField = useCallback(
    (field: keyof FormFields, value: string) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      setFormErrors((prev) => ({ ...prev, [field]: false }));
    },
    [],
  );

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submissionPending.current || formSubmitted) return;

    const splitLayout = isSplitColumnContactVariant(variant);
    const errors = validateForm(formData, {
      splitName: splitLayout,
      requireCompanyWebsite: splitLayout,
    });
    setFormErrors(errors);
    const privacyMissing = !privacyPolicyAccepted;
    setPrivacyPolicyError(privacyMissing);
    if (Object.keys(errors).length > 0 || privacyMissing) return;

    submissionPending.current = true;
    setIsSubmitting(true);
    setSubmitError(false);

    const journey = getVisitorJourney();
    const serviceLabels = selectedServices.map((value) => {
      const option = servicePillOptions.find((item) => item.value === value);
      return option ? t(option.label, lang) : value;
    });
    const budgetOption = budgetPillOptions.find(
      (item) => item.value === selectedBudget,
    );
    const budgetLabel = budgetOption
      ? t(budgetOption.label, lang)
      : selectedBudget;
    const messageBody = appendSubmissionMeta(
      formData.message.trim(),
      selectedBudget,
      serviceLabels,
      budgetLabel,
    );

    try {
      const response = await fetch("/api/proxy", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: submissionName(formData, isSplitColumnContactVariant(variant)),
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          company: formData.company.trim(),
          website: formData.website.trim(),
          body: messageBody,
          ...(selectedServices.length > 0 ? { services: selectedServices } : {}),
          ...(selectedBudget ? { budget: selectedBudget } : {}),
          type: "formSubmission",
          locale: lang,
          privacyPolicyAccepted,
          language: navigator.language,
          tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
          source: window.location.href,
          journey,
          ourCompany: getOurCompany(),
        }),
      });

      const result = await response.json();
      if (!response.ok || result.received !== true) {
        setSubmitError(true);
        return;
      }

      setFormSubmitted(true);
      trackContactLead(lang);
    } catch {
      setSubmitError(true);
    } finally {
      submissionPending.current = false;
      setIsSubmitting(false);
    }
  }

  const isSplitLayout = isSplitColumnContactVariant(variant);
  const isDarkSplit = variant === "dark-split";
  const isParallax = variant === "parallax-panel";
  const elevateParallaxFooter = isParallax && formTheme === "dark";
  const panelClassName = `cc-contact__panel${
    isParallax ? " cc-contact__panel--parallax-card" : ""
  }${isDarkSplit ? " cc-contact__panel--dark-split-card" : ""}${
    isSplitLayout ? " cc-contact__panel--light-split" : ""
  }`;
  const gridClassName = isParallax
    ? "cc-contact__parallax-layout"
    : isSplitLayout
      ? "cc-contact__grid cc-contact__grid--light-split"
      : "cc-contact__grid";

  const formFooter = (
    <div
      className={`cc-contact__form-footer${
        isSplitLayout ? " cc-contact__form-footer--light-split" : ""
      }${elevateParallaxFooter ? " cc-contact__form-footer--parallax-elevated" : ""}`}
    >
      <label
        className={`cc-contact__consent${privacyPolicyError ? " cc-contact__consent--invalid" : ""}`}
      >
        <CcAnimatedCheckbox
          name="privacyPolicyAccepted"
          checked={privacyPolicyAccepted}
          onCheckedChange={(checked) => {
            setPrivacyPolicyAccepted(checked);
            setPrivacyPolicyError(false);
          }}
          required
          aria-invalid={privacyPolicyError}
        />
        <span className="cc-contact__consent-label">
          {t(formCopy.acceptPrivacyPolicyBefore, lang)}
          <CcInternalLink
            href="/privacy-policy"
            className="cc-contact__consent-link"
            onClick={(event) => event.stopPropagation()}
          >
            {t(pageCopy.footer.privacyPolicy, lang)}
          </CcInternalLink>
        </span>
      </label>
      <div className="cc-contact__actions">
        <CcButton
          type="submit"
          variant="primary"
          loading={isSubmitting}
          disabled={isSubmitting || formSubmitted}
          label={
            isSubmitting
              ? t(formCopy.sending, lang)
              : t(isSplitLayout ? formCopy.ctaSend : formCopy.cta, lang)
          }
          className={`cc-contact__submit${
            isSplitLayout
              ? " cc-contact__submit--full"
              : " cc-hero__action cc-hero__action--primary"
          }`}
        />
        <button
          type="button"
          className="cc-btn cc-btn--outline cc-contact__book-meeting"
          disabled={isSubmitting}
          data-book-call-icon="calendar"
          onClick={(event) => {
            const fullName = submissionName(formData, isSplitLayout);
            const [firstName = "", ...lastName] = fullName.split(/\s+/);
            const labels = selectedServices.map((value) => {
              const option = servicePillOptions.find((item) => item.value === value);
              return option ? t(option.label, lang) : value;
            });
            const budget = budgetPillOptions.find((item) => item.value === selectedBudget);
            bookCall.open(bookCallOriginFromElement(event.currentTarget), {
              prefill: {
                firstName: isSplitLayout ? formData.firstName.trim() : firstName,
                lastName: isSplitLayout ? formData.lastName.trim() : lastName.join(" "),
                email: formData.email.trim(),
                company: formData.company.trim(),
                website: formData.website.trim(),
                notes: appendSubmissionMeta(
                  formData.message.trim(),
                  selectedBudget,
                  labels,
                  budget ? t(budget.label, lang) : selectedBudget,
                ),
              },
            });
          }}
        >
          {t(formCopy.bookMeetingInstead, lang)}
        </button>
      </div>
    </div>
  );

  const pageHeader = isSplitLayout ? (
    <header
      className={`cc-contact__page-header${
        isDarkSplit ? " cc-contact__page-header--on-dark-hero" : ""
      }`}
    >
      <h1
        id="contact-page-title"
        className={
          isDarkSplit
            ? "cc-contact__page-title cc-contact__headline--page"
            : "cc-hero__title"
        }
        dangerouslySetInnerHTML={{
          __html: tHtml(contactCopy.lightTitle, lang),
        }}
      />
      <p className="cc-contact__page-intro">{t(contactCopy.lightLead, lang)}</p>
    </header>
  ) : null;

  const introColumn = isSplitLayout ? (
    <div
      className={`cc-contact__intro cc-contact__intro--light-aside${
        isDarkSplit ? " cc-contact__intro--dark-aside" : ""
      }`}
    >
      <CcContactSidebar lang={lang} mode={isDarkSplit ? "dark-aside" : "light"} />
    </div>
  ) : (
    <div className="cc-contact__intro">
      <CcSanjayaEyebrow theme="on-dark" className="cc-contact__eyebrow">
        {t(contactCopy.eyebrow, lang)}
      </CcSanjayaEyebrow>
      <h1
        id="contact-page-title"
        className="cc-contact__headline cc-contact__headline--page"
        dangerouslySetInnerHTML={{
          __html: tHtml(contactCopy.title, lang),
        }}
      />
      <p className="cc-contact__lead">{t(contactCopy.heroLead, lang)}</p>
      {isParallax ? (
        <div className="cc-contact__intro-logos">
          <CcHeroLogoBand caption={commonCopy.logoBandCaption} />
        </div>
      ) : (
        <CcContactSidebar lang={lang} />
      )}
    </div>
  );

  const formPanel = (
            <div
              className={panelClassName}
              data-cc-contact-form-theme={formTheme}
            >
              {isParallax ? (
                <CcInternalLink
                  href="/"
                  className="cc-contact__panel-logo"
                  aria-label={commonCopy.siteHomeAria}
                >
                  <Image
                    src="/assets/imgs/nav-mark.svg"
                    alt=""
                    width={20}
                    height={21}
                  />
                </CcInternalLink>
              ) : null}
              <form
                className="cc-contact__form"
                data-analytics-form="contact"
                onSubmit={handleSubmit}
                aria-busy={isSubmitting}
                noValidate
                aria-labelledby="contact-form-title"
              >
                <div
                  className={
                    isParallax
                      ? "cc-contact__form-body cc-contact__form-body--parallax"
                      : "cc-contact__form-body"
                  }
                >
                  {!isSplitLayout ? (
                    <header className="cc-contact__form-header">
                      {panelEyebrow ? (
                        <CcSanjayaEyebrow
                          theme={isParallax ? "on-dark" : "on-subtle"} className="cc-contact__form-eyebrow"
                       >
                          {panelEyebrow}
                        </CcSanjayaEyebrow>
                      ) : null}
                      <h2
                        id="contact-form-title"
                        className="cc-contact__form-title cc-contact__headline--form"
                        dangerouslySetInnerHTML={{
                          __html: tHtml(formCopy.title, lang),
                        }}
                      />
                      {panelLead ? (
                        <p className="cc-contact__form-lead">{panelLead}</p>
                      ) : null}
                    </header>
                  ) : (
                    <h2 id="contact-form-title" className="sr-only">
                      {t(formCopy.title, lang)}
                    </h2>
                  )}
                <div
                  className={`cc-contact__form-grid${
                    isSplitLayout ? " cc-contact__form-grid--light-split" : ""
                  }`}
                >
                  {isSplitLayout ? (
                    <>
                      <div
                        className={cellClass(
                          "cc-contact__cell cc-contact__cell--first-name",
                          Boolean(formErrors.firstName),
                        )}
                      >
                        <label
                          className="cc-contact__label"
                          htmlFor="contact-first-name"
                        >
                          {t(formCopy.firstName, lang)}
                        </label>
                        <input
                          id="contact-first-name"
                          name="firstName"
                          type="text"
                          className="cc-contact__input"
                          placeholder={t(formCopy.firstNamePlaceholder, lang)}
                          value={formData.firstName}
                          onChange={(e) => updateField("firstName", e.target.value)}
                          required
                          autoComplete="given-name"
                        />
                      </div>
                      <div
                        className={cellClass(
                          "cc-contact__cell cc-contact__cell--last-name",
                          Boolean(formErrors.lastName),
                        )}
                      >
                        <label
                          className="cc-contact__label"
                          htmlFor="contact-last-name"
                        >
                          {t(formCopy.lastName, lang)}
                        </label>
                        <input
                          id="contact-last-name"
                          name="lastName"
                          type="text"
                          className="cc-contact__input"
                          placeholder={t(formCopy.lastNamePlaceholder, lang)}
                          value={formData.lastName}
                          onChange={(e) => updateField("lastName", e.target.value)}
                          required
                          autoComplete="family-name"
                        />
                      </div>
                      <div
                        className={cellClass(
                          "cc-contact__cell cc-contact__cell--email cc-contact__cell--full",
                          Boolean(formErrors.email),
                        )}
                      >
                        <label
                          className="cc-contact__label"
                          htmlFor="contact-email"
                        >
                          {t(formCopy.emailAddress, lang)}
                        </label>
                        <input
                          id="contact-email"
                          name="email"
                          type="email"
                          className="cc-contact__input"
                          placeholder={t(formCopy.emailPlaceholder, lang)}
                          value={formData.email}
                          onChange={(e) => updateField("email", e.target.value)}
                          required
                          autoComplete="email"
                        />
                      </div>
                    </>
                  ) : (
                    <div
                      className={cellClass(
                        "cc-contact__cell cc-contact__cell--name",
                        Boolean(formErrors.name),
                      )}
                    >
                      <label className="cc-contact__label" htmlFor="contact-name">
                        {t(formCopy.name, lang)}
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        className="cc-contact__input"
                        placeholder={t(formCopy.namePlaceholder, lang)}
                        value={formData.name}
                        onChange={(e) => updateField("name", e.target.value)}
                        required
                        autoComplete="name"
                      />
                    </div>
                  )}
                  {!isSplitLayout ? (
                    <div
                      className={cellClass(
                        "cc-contact__cell cc-contact__cell--email",
                        Boolean(formErrors.email),
                      )}
                    >
                      <label className="cc-contact__label" htmlFor="contact-email">
                        {t(formCopy.emailAddress, lang)}
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        className="cc-contact__input"
                        placeholder={t(formCopy.emailPlaceholder, lang)}
                        value={formData.email}
                        onChange={(e) => updateField("email", e.target.value)}
                        required
                        autoComplete="email"
                      />
                    </div>
                  ) : null}
                  <div
                    className={cellClass(
                      "cc-contact__cell cc-contact__cell--company",
                      Boolean(formErrors.company),
                    )}
                  >
                    <label className="cc-contact__label" htmlFor="contact-company">
                      {t(formCopy.company, lang)}
                    </label>
                    <input
                      id="contact-company"
                      name="company"
                      type="text"
                      className="cc-contact__input"
                      placeholder={t(formCopy.companyPlaceholder, lang)}
                      value={formData.company}
                      onChange={(e) => updateField("company", e.target.value)}
                      autoComplete="organization"
                      required={isSplitLayout}
                    />
                  </div>
                  <div
                    className={cellClass(
                      "cc-contact__cell cc-contact__cell--website",
                      Boolean(formErrors.website),
                    )}
                  >
                    <label className="cc-contact__label" htmlFor="contact-website">
                      {t(formCopy.website, lang)}
                    </label>
                    <input
                      id="contact-website"
                      name="website"
                      type="url"
                      className="cc-contact__input"
                      placeholder={t(formCopy.websitePlaceholder, lang)}
                      value={formData.website}
                      onChange={(e) => updateField("website", e.target.value)}
                      autoComplete="url"
                      inputMode="url"
                      required={isSplitLayout}
                    />
                  </div>
                  <div
                    className={cellClass(
                      "cc-contact__cell cc-contact__cell--message",
                      Boolean(formErrors.message),
                    )}
                  >
                    <label className="cc-contact__label" htmlFor="contact-message">
                      {t(
                        isSplitLayout ? formCopy.messageLabel : formCopy.messageAbout,
                        lang,
                      )}
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      className="cc-contact__input cc-contact__textarea"
                      placeholder={t(
                        isSplitLayout
                          ? formCopy.messagePlaceholderClean
                          : formCopy.messagePlaceholder,
                        lang,
                      )}
                      rows={showExtendedFields ? 4 : 3}
                      value={formData.message}
                      onChange={(e) => updateField("message", e.target.value)}
                      required
                    />
                  </div>
                  {showExtendedFields && isSplitLayout ? (
                    <fieldset className="cc-contact__cell cc-contact__cell--services">
                      <legend className="cc-contact__label">
                        {t(formCopy.servicesHeading, lang)}
                      </legend>
                      <div className="cc-contact__services-checkboxes cc-contact__services-checkboxes--grid">
                        {servicePillOptions.map((option) => {
                          const checked = selectedServices.includes(option.value);
                          return (
                            <label
                              key={option.value}
                              className={`cc-contact__service-check${
                                checked ? " cc-contact__service-check--selected" : ""
                              }`}
                            >
                              <CcAnimatedCheckbox
                                name={`service-${option.value}`}
                                checked={checked}
                                onCheckedChange={(next) => {
                                  setSelectedServices((prev) =>
                                    next
                                      ? [...prev, option.value]
                                      : prev.filter((v) => v !== option.value),
                                  );
                                }}
                              />
                              <span className="cc-contact__service-check-label">
                                {t(option.label, lang)}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>
                  ) : null}
                  {showExtendedFields && !isSplitLayout ? (
                    <>
                      <CcContactPillField
                        lang={lang}
                        id="contact-services"
                        label={formCopy.servicesLabel}
                        options={servicePillOptions}
                        mode="multiple"
                        value={selectedServices}
                        onChange={(value) =>
                          setSelectedServices(Array.isArray(value) ? value : [])
                        }
                      />
                      {showBudgetFields ? (
                        <CcContactPillField
                          lang={lang}
                          id="contact-budget"
                          label={formCopy.budgetLabel}
                          options={budgetPillOptions}
                          mode="single"
                          value={selectedBudget}
                          onChange={(value) =>
                            setSelectedBudget(typeof value === "string" ? value : "")
                          }
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>
                  <div className="cc-contact__status" aria-live="polite" aria-atomic="true">
                    <p
                      className={`cc-contact__status-message cc-contact__error${submitError ? " cc-contact__status-message--visible" : ""}`}
                      role={submitError ? "alert" : undefined}
                      aria-hidden={!submitError}
                    >
                      {t(formCopy.submitError, lang)}
                    </p>
                    <p
                      className={`cc-contact__status-message cc-contact__success${formSubmitted ? " cc-contact__status-message--visible" : ""}`}
                      aria-hidden={!formSubmitted}
                    >
                      {t(contactCopy.thankYou.description, lang)}
                    </p>
                  </div>
                </div>
                {isParallax ? <CcContactParallaxFormFade /> : null}
                {formFooter}
              </form>
            </div>
  );

  const formLayout = isSplitLayout ? (
    <>
      {pageHeader}
      <div className={gridClassName}>
        {formPanel}
        {introColumn}
      </div>
    </>
  ) : (
    <div className={gridClassName}>
      {introColumn}
      {formPanel}
    </div>
  );

  return (
    <section
      className="cc-contact cc-section"
      data-cc-contact-variant={variant}
      aria-labelledby="contact-page-title"
      {...devSectionAttr("contact")}
    >
      {variant === "light-split" ? (
        <div className="cc-contact__frame cc-contact__frame--light" data-cc-nav="light">
          <div className="cc-contact__shell cc-contact__shell--light">
            {formLayout}
          </div>
        </div>
      ) : isDarkSplit ? (
        <div
          className="cc-contact__frame cc-contact__frame--dark-split"
          data-cc-nav="dark"
        >
          <CcContactAntiqueHeroBackdrop showAttachedFooterSeam darkSplitScrim />
          <div className="cc-contact__shell cc-contact__shell--dark-split">
            {formLayout}
          </div>
        </div>
      ) : isParallax ? (
        <CcContactParallaxShell>
          <div
            className="cc-contact__shell cc-contact__shell--parallax"
            data-cc-nav="dark"
          >
            {formLayout}
          </div>
        </CcContactParallaxShell>
      ) : (
        <div className="cc-contact__frame" data-cc-nav="dark">
          <div className="cc-contact__media" aria-hidden="true">
            <CcAntiqueHeroPicture fetchPriority="high" sourceOrder="webp-first" />
          </div>
          <div className="cc-contact__shell">{formLayout}</div>
        </div>
      )}
    </section>
  );
}
